import { createContext, useContext, useEffect, useState } from "react";
import { useGetData } from "../hooks/useGetData";
import { AuthContext } from "./AuthContext";

interface BloquesContextProps {
  verificarBloqueActivo: (id: number) => boolean;
  bloquesActivos: number[];
  isLoading: boolean;
  refetchBloques: () => void;
}

const BloquesContext = createContext<BloquesContextProps | undefined>(undefined);

export const BloquesProvider = ({ children }: { children: React.ReactNode }) => {
  const [bloquesActivos, setBloquesActivos] = useState<number[]>([]);
  const { usuario, isLoggedIn, token } = useContext(AuthContext);

  // Solo hacer la consulta si el usuario está completamente autenticado
  const shouldFetch = isLoggedIn && !!usuario && !!usuario.id && !!token;

  console.log("🔍 BloquesProvider - Estado inicial:", {
    isLoggedIn,
    usuario: usuario?.id,
    token: !!token,
    shouldFetch
  });

  // Query para obtener los bloques del usuario logueado
  const queryBloquesUsuario = useGetData<{ id: number }[]>({
    url: "/mis-bloques",
    name: ["usuario-bloques", usuario?.id?.toString() || ""],
    condition: shouldFetch,
  });

  useEffect(() => {
    console.log("🔍 BloquesContext - Query data changed:", queryBloquesUsuario.data);
    console.log("🔍 BloquesContext - Query error:", queryBloquesUsuario.error);
    console.log("🔍 BloquesContext - Query loading:", queryBloquesUsuario.isLoading);
    
    if (queryBloquesUsuario.data && usuario?.id) {
      const activos = queryBloquesUsuario.data.map((bloque) => bloque.id);
      console.log("🔍 BloquesContext - Bloques activos:", activos);
      setBloquesActivos(activos);
      
      // Guardar en localStorage con clave específica del usuario
      const storageKey = `bloquesActivos_${usuario.id}`;
      localStorage.setItem(storageKey, JSON.stringify(activos));
      console.log("🔍 BloquesContext - Guardado en localStorage:", storageKey, activos);
    }
  }, [queryBloquesUsuario.data, usuario?.id]);

  // Cargar desde localStorage al inicializar
  useEffect(() => {
    if (usuario?.id && isLoggedIn) {
      const storageKey = `bloquesActivos_${usuario.id}`;
      const bloquesStorage = localStorage.getItem(storageKey);
      if (bloquesStorage) {
        try {
          const bloques = JSON.parse(bloquesStorage);
          setBloquesActivos(bloques);
        } catch (error) {
          console.error("Error parsing bloques from localStorage:", error);
          localStorage.removeItem(storageKey);
        }
      }
    }
  }, [usuario?.id, isLoggedIn]);

  // Limpiar localStorage cuando el usuario cambie o se desloguee
  useEffect(() => {
    if (!isLoggedIn || !usuario) {
      setBloquesActivos([]);
      // Limpiar todos los bloques del localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('bloquesActivos_')) {
          localStorage.removeItem(key);
        }
      });
    }
  }, [isLoggedIn, usuario]);

  const verificarBloqueActivo = (id: number) => bloquesActivos.includes(id);

  const refetchBloques = () => {
    console.log("🔄 RefetchBloques llamado - shouldFetch:", shouldFetch);
    if (shouldFetch) {
      queryBloquesUsuario.refetch().then(() => {
        console.log("🔄 Bloques refrescados exitosamente");
      }).catch((error) => {
        console.error("🔄 Error refrescando bloques:", error);
      });
    }
  };

  return (
    <BloquesContext.Provider
      value={{ 
        verificarBloqueActivo, 
        bloquesActivos, 
        isLoading: queryBloquesUsuario.isLoading,
        refetchBloques
      }}
    >
      {children}
    </BloquesContext.Provider>
  );
};

export const useBloquesContext = () => {
  const context = useContext(BloquesContext);
  if (!context) {
    throw new Error("useBloquesContext debe usarse dentro de un BloquesProvider");
  }
  return context;
};